import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import socketService from '../services/socketService';
import { addNotification } from '../redux/notificationsSlice';
import { useNotificationAudio } from './useNotificationAudio';

export function useNotifications() {
  const dispatch = useDispatch();
  const { playNotification } = useNotificationAudio();

  const token = useSelector((state) => state.user?.accessToken || state.user?.token);
  const user = useSelector((state) => state.user?.profile || state.user?.user);

  const listenersRegistered = useRef(false);

  // Stable refs — always point to latest values without changing identity
  const playRef = useRef(playNotification);
  const dispatchRef = useRef(dispatch);

  useEffect(() => { playRef.current = playNotification; }, [playNotification]);
  useEffect(() => { dispatchRef.current = dispatch; }, [dispatch]);

  // Stable handler — never changes identity
  const handleNewNotification = useRef((data) => {
    dispatchRef.current(addNotification(data));
    playRef.current();

    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(data.title || 'New Notification', {
        body: data.message,
        icon: '/logo.png',
        badge: '/logo.png',
        tag: data.type || 'general',
      });
    }
  }).current;

  useEffect(() => {
    if (!token || !user) return;
    if (listenersRegistered.current) return;

    const socket = socketService.connect(token);
    socket.onAny(() => {});

    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // Company events
    socketService.on('company:created', (data) => {
      handleNewNotification({
        type: 'company',
        title: 'New Company',
        message: `Company "${data.company_name}" created`,
        data: {
          id: data.id,
          company_name: data.company_name,
          status: data.status,
          admin: data.admin,
          createdAt: data.createdAt,
        },
      });
    });

    // Suggestion events
    socketService.on('suggestion:new', (data) => {
      handleNewNotification({
        type: 'suggestion',
        title: 'New Suggestion',
        message: data.name || 'New suggestion received',
        data: {
          id: data.id,
          name: data.name,
        },
      });
    });

    // Subscription events
    socketService.on('subscription:created', (data) => {
      handleNewNotification({
        type: 'subscription',
        title: 'New Subscription',
        message: `${data.companyName} - ${data.planTitle} (${data.amount} ${data.currency})`,
        data: {
          companyId: data.companyId,
          subscriptionId: data.subscriptionId,
          companyName: data.companyName,
          planTitle: data.planTitle,
          amount: data.amount,
          currency: data.currency,
          duration: data.duration,
          timestamp: data.timestamp,
        },
      });
    });

    // Contact message events (bug / issue / improvement reported by an admin)
    socketService.on('contact:new', (data) => {
      handleNewNotification({
        type: 'contact',
        title: 'New Admin Message',
        message: data.message || `New message from ${data.name || data.email}`,
        data: {
          id: data.id,
          name: data.name,
          email: data.email,
          companyName: data.companyName,
        },
      });
    });

    // Payment proof events
    socketService.on('payment-proof:submitted', (data) => {
      handleNewNotification({
        type: 'payment-proof',
        title: data.isResubmission ? 'Payment Proof Resubmitted' : 'New Payment Proof',
        message: data.isResubmission
          ? `Resubmission: ${data.companyName} (${data.amount} ${data.currency})`
          : `${data.companyName} - ${data.amount} ${data.currency}`,
        data: {
          companyId: data.companyId,
          subscriptionId: data.subscriptionId,
          companyName: data.companyName,
          amount: data.amount,
          currency: data.currency,
          isResubmission: data.isResubmission,
          uploadedAt: data.uploadedAt,
        },
      });
    });

    listenersRegistered.current = true;

    return () => {
      socketService.off('company:created');
      socketService.off('suggestion:new');
      socketService.off('contact:new');
      socketService.off('subscription:created');
      socketService.off('payment-proof:submitted');
      listenersRegistered.current = false;
    };
  }, [token, user]); // handleNewNotification removed from deps — stable via ref

  useEffect(() => {
    if (!token) {
      socketService.disconnect();
      listenersRegistered.current = false;
    }
  }, [token]);

  return { isConnected: socketService.isConnected() };
}